import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Img } from 'react-image';
import PlacementModeComponent from './PlacementModeComponent';
import TrackerSwitcherComponent from './TrackerSwitcherComponent';
import ModelTrackerCameraPreview from './ModelTrackerCameraPreview';
import { SimulationStates, CustomEventTypes, TrackerTypes, LayoutOrientations } from '../../globals';
import ARTracker from '../../classes/ARTracker';
import Engine from '../../classes/Engine';
import Scenario from '../../classes/scenarioElements/Scenario';
import store, { StoreActions } from '../../state/store';
import { useTypedSelector } from '../../state/hooks';
import FullScreenImagePreview from '../visageUI/FullScreenImagePreview';
import Spacer from '../layout/Spacer';
import Panel from '../layout/Panel';
import AssetUtility from '../../classes/AssetUtility';
import { Spinner } from '../Iconset';
import Step from '../../classes/scenarioElements/Step';
import useForceUpdate from '../reactUtils/useForceUpdate';
import useEventListener from '../reactUtils/useEventListener';
import useCurrentStep from '../reactUtils/useCurrentStep';

type TrackerPreviewUpdateEventPayload = { 
	guideImageAssetId: string, 
	guideTitle: string, 
	guideCaption: string, 
	guideSize: number,
	guideCaptionVisible: boolean,
	guideTitleVisible: boolean, 
};

export default function TrackersPreviewContainer()
{
	useEventListener(CustomEventTypes.onTrackerListUpdated, handleTrackerlistUpdate, document);
	useEventListener(CustomEventTypes.onTrackerPreviewUpdated, handleTrackerPreviewUpdate, document);
	useEventListener(CustomEventTypes.objectSelectionChanged, handleTrackerlistUpdate, document);
	useEventListener(CustomEventTypes.objectNameChanged, () => forceUpdate(), document);

	const forceUpdate = useForceUpdate();

	const currentStep = useCurrentStep();
	const simulationState = useTypedSelector(state => state.applicationModule.simulationState, shallowEqual);
	const editingSlide = useTypedSelector(state => state.uiModule.editingSlide, shallowEqual);

	const [trackerList, setTrackerList] = useState<Array<ARTracker>>([]);
	const [selectedTracker, setSelectedTracker] = useState(trackerList?.at(0));
	const [selectedTrackerType, setSelectedTrackerType] = useState(selectedTracker?.trackerType);
	const [fullscreen, setFullScreen] = useState("");
	const [guideImageAssetId, setGuideImageAssetId] = useState("");
	const [guideTitle, setGuideTitle] = useState("");
	const [guideCaption, setGuideCaption] = useState("");
	const [guideSize, setGuideSize] = useState(1);
	const [guideCaptionVisible, setGuideCaptionVisible] = useState(true);
	const [guideTitleVisible, setGuideTitleVisible] = useState(true);

	const trackerGuideDefaultSize = 150; // when the tracker.guideSize is 1, the container width will be set to 150px

	useEffect(() => 
	{
		setGuideImageAssetId(selectedTracker?.getGuideImageAssetId() || "");
		setGuideTitle(selectedTracker?.getGuideTitle() || "");
		setGuideCaption(selectedTracker?.getGuideCaption() || "");
		setGuideSize(selectedTracker?.guideSize || 1);
	}, [selectedTracker]);

	useEffect(() =>
	{
		if(simulationState === SimulationStates.Playing && !currentStep.simulationSkip)
		{
			Scenario.clearSimulationSkips();
			currentStep.simulationSkip = true;
		}

		handleTrackerlistUpdate();
	}, [currentStep, editingSlide]);

	useEffect(() =>
	{
		if(!trackerList || !selectedTrackerType) return;

		for(const tracker of trackerList)
		{
			if(tracker.trackerType === selectedTrackerType) 
			{
				setSelectedTracker(tracker);
				break;
			}
		}
	}, [selectedTrackerType]);

	function handleTrackerlistUpdate()
	{
		const trackers = ARTracker.getTrackerList();
		setTrackerList(trackers);

		const currentSelection = new Array<ARTracker>();
		ARTracker.getTrackerList().forEach(tracker => {if(Engine.getInstance().getPickingManager().isObjectSelected(tracker)) currentSelection.push(tracker)});
		setSelectedTracker(currentSelection.length ? currentSelection[0] : trackers?.at(0));
	}

	function handleTrackerPreviewUpdate(e: CustomEvent<TrackerPreviewUpdateEventPayload>)
	{
		setGuideImageAssetId(e.detail.guideImageAssetId);
		setGuideTitle(e.detail.guideTitle);
		setGuideCaption(e.detail.guideCaption);
		setGuideSize(e.detail.guideSize);
		setGuideCaptionVisible(e.detail.guideCaptionVisible);
		setGuideTitleVisible(e.detail.guideTitleVisible);
	}

	function goToNextStep()
	{
		let nextStep: Step | undefined;

		if(store.getState().uiModule.nextStepOverride)
		{
			nextStep = Scenario.getStep(store.getState().uiModule.nextStepOverride);
			store.dispatch(StoreActions.ui.setNextStepOverride(""));
		}
		else
		{
			nextStep = Scenario.currentStep()?.getNextStep();
		}
						
		if (nextStep)
		{
			Scenario.setCurrentStep(nextStep);
		}
	}

	const trackerTitle = useMemo(() => 
	{
		if(selectedTracker)
		{
			switch(selectedTracker.trackerType)
			{
				case TrackerTypes.IMAGE:
					return 'Point your device at the target image';
				case TrackerTypes.MODEL:
					if(selectedTracker.autoInit)
						return `Point the camera at ${selectedTracker.name}`;
					return 'Match the outline to the object';
				case TrackerTypes.SLAM:
					return 'Move your device around slowly';
			}
		}

		return 'No trackers found';
	}, [selectedTracker]);

	return (
		<Panel data-name={"trackers-preview-container"} 
			className={"fullScreen no-overflow"} 
			data-acceptmouseinput={"false"}
			orientation={LayoutOrientations.Vertical}
		>
			<div className={'trackersPreviewContent centerContent'}>
				{
					selectedTracker?.trackerType === TrackerTypes.MODEL && !selectedTracker.autoInit &&
						<ModelTrackerCameraPreview
							tracker={selectedTracker}
							hideEditButton={true}
							simulationState={simulationState}
							contained={true}
						/>
				}
			</div>
			{
				guideImageAssetId &&
					<div className={"trackerGuideRoot centerContent"}>
						<div className={"trackerGuideContainer"}
							style={{
								width: `${trackerGuideDefaultSize * guideSize}px`,
							}}
						>
							{
								guideTitle && guideTitleVisible &&
									<div className={"trackerGuideTitle"}>
										{ guideTitle }
									</div>
							}
							<div className={"trackerGuideImage"}
								onClick={() => 
								{
									setFullScreen(AssetUtility.getPreviewAssetURL(guideImageAssetId))
								}}
							>
								<Img className={"trackersPreviewGuideImage"} 
									loader={<Spinner />}
									src={AssetUtility.getPreviewAssetURL(guideImageAssetId)} 
								/>
							</div>
							{
								guideCaption && guideCaptionVisible &&
									<div className={"trackerGuideCaption"}>
										{ guideCaption }
									</div>
							}
						</div>
					</div>
			}
			<div className={'trackersSwitcher'}>
				<TrackerSwitcherComponent
					currentTracker={selectedTracker}
					trackerList={trackerList}
					onTrackerSwitch={(tracker: ARTracker) => setSelectedTracker(tracker)}
				/>
			</div>
			<Spacer stretchTo={{ y: "100%" }} />
			<div className='trackersPlacementContainer'>
				<div data-name={"trackers-placement-instructions"}
					className={'trackersPlacementInstructions'}
				>
					{trackerTitle}
				</div>
				<Spacer size={{ y: "18px" }} />
				{
					trackerList?.length > 0 &&
						<PlacementModeComponent
							currentType={selectedTracker?.trackerType}
							trackerList={trackerList}
							onTrackerTypeSwitch={(type: TrackerTypes) => setSelectedTrackerType(type)}
						/>
				}
			</div>
			<div className={"uiButtonStep next"}
				data-name='ui-preview-next-step'
			>
				<img alt="next"
					src='./svg/next.svg'
					onClick={goToNextStep}
				/>
			</div>
			{
				fullscreen !== "" &&
					<FullScreenImagePreview assetSrc={fullscreen} onClose={() => setFullScreen("")}/> 
			}
		</Panel>
	);
}